import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  forkJoin as observableForkJoin,
  of as observableOf,
  type Observable,
} from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { AuthFacade } from '../../../auth/store/auth.facade';
import { PropertiesSharedService } from '../../../core/services/properties.service';
import { SupplierManagementHelperService } from '../../../core/services/supplier-management-helper.service';
import type { GetSupplierPropertiesResponseDto } from '../../../insights/suppliers/models/supplier-properties.dto.models';
import type { SupplierFilter } from '../../../insights/suppliers/models/supplier.models';
import type { CategoryTree } from '../../../insights/suppliers/modules/categorization/models/category-tree.models';
import { CategoryTreeService } from '../../../insights/suppliers/modules/categorization/services/category-tree.service';
import { SuppliersService } from '../../../insights/suppliers/services/suppliers.service';
import { AccountService } from '../../../management/accounts/services/account.service';
import { CompanyService } from '../../../management/companies/services/company.service';
import { Modules } from '../../consts/custom-fields.consts';
import { assert } from '../../helpers/assert/assert.helper';
import { slimSupplierFilter } from '../../helpers/create-slim-supplier-filter/slim-supplier-filter.helper';
import { enrichSupplierFilter } from '../../helpers/enrich-supplier-filter/enrich-supplier-filter.helper';
import { isArray } from '../../helpers/is-array/is-array.helper';
import { isNotNullOrUndefined } from '../../helpers/is-not-null-or-undefined/is-not-null-or-undefined.helpers';
import type { ApiResponse } from '../../models/response';
import {
  getPropertiesAndFilters,
  getPropertiesAndFiltersFail,
  getPropertiesAndFiltersSuccess,
  loadAccounts,
  loadAccountsFail,
  loadAccountsSuccess,
  loadCompanies,
  loadCompaniesFail,
  loadCompaniesSuccess,
  updateSupplierFilters,
  updateSupplierFiltersFail,
  updateSupplierFiltersSuccess,
} from '../actions/company-account.actions';
import {
  showFailMessage,
  showSuccessMessage,
} from '../actions/show-message.actions';

type PropertiesApiResponses = [
  Observable<GetSupplierPropertiesResponseDto>,
  Observable<ApiResponse<{ filters?: Array<Partial<SupplierFilter>> }>>,
  Observable<ApiResponse<CategoryTree[]>>,
];

@Injectable()
export class CompanyAccountEffects {
  readonly #accountService = inject(AccountService);
  readonly #actions$ = inject(Actions);
  readonly #authFacade = inject(AuthFacade);
  readonly #authService = inject(AuthService);
  readonly #categoryTreeService = inject(CategoryTreeService);
  readonly #companyService = inject(CompanyService);
  readonly #propertiesSharedService = inject(PropertiesSharedService);
  readonly #suppliersService = inject(SuppliersService);
  readonly #translate = inject(TranslocoService);

  readonly loadAllAccounts$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(loadAccounts),
      map((data) => data.payload),
      switchMap((config) =>
        this.#accountService.getAccounts(config).pipe(
          map((response) => {
            if (response.status) {
              assert(
                isNotNullOrUndefined(response.data),
                'Response data is not available',
              );

              return loadAccountsSuccess({
                payload: response.data,
              });
            }
            return loadAccountsFail({ payload: response });
          }),
          catchError((error) =>
            observableOf(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
              loadAccountsFail({ payload: error }),
            ),
          ),
        ),
      ),
    );
  });

  readonly loadAllCompanies$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(loadCompanies),
      map((data) => data.payload),
      switchMap((config) =>
        this.#companyService.getFilteredCompanies(config).pipe(
          map((response) => {
            if (response.status) {
              assert(
                isNotNullOrUndefined(response.data),
                'Response data is not available',
              );

              return loadCompaniesSuccess({
                payload: response.data,
              });
            }
            return loadCompaniesFail({ payload: response });
          }),
          catchError((error) =>
            observableOf(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
              loadCompaniesFail({ payload: error }),
            ),
          ),
        ),
      ),
    );
  });

  readonly getProperties$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(getPropertiesAndFilters),
      map((data) => data.payload),
      concatLatestFrom(
        () => this.#authFacade.isLegacyCategoriesModuleActivated$,
      ),
      // eslint-disable-next-line unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
      switchMap(([params, isLegacyCategoriesModuleActivated]) => {
        const { accounts } = params;
        const accountId = accounts?.[0];

        // NOTE: Extract default data from user profile for fallback scenarios
        const firstAreaId = this.#authService.profile?.contentAreas?.[0]?.id;
        const allDefaultFilters = this.#authService.profile?.allDefaultFilters;
        const defaultFilters = this.#authService.profile?.defaultFilters;

        // NOTE: Default responses for multi-account or no-account selection (uses profile defaults)
        const defaultPropertiesDto = {
          status: true,
          data: {
            [String(firstAreaId ?? '')]:
              isArray(allDefaultFilters) ? allDefaultFilters : [],
          },
          message: '',
        } satisfies GetSupplierPropertiesResponseDto;

        let responses: PropertiesApiResponses = [
          observableOf(defaultPropertiesDto),
          observableOf({
            status: true,
            data: { filters: defaultFilters },
            message: '',
          }),
          observableOf({ status: true, data: [], message: '' }),
        ];

        // NOTE: Single account selected - fetch real data from API and user profile
        if (accountId !== undefined) {
          // NOTE: Get account-specific filters from user profile, fallback to default filters
          let filtersForAccount: Array<Partial<SupplierFilter>> | undefined =
            this.#authService.profile?.activeFiltersPerAccount?.[accountId] as
              | Array<Partial<SupplierFilter>>
              | undefined;
          if (!isLegacyCategoriesModuleActivated) {
            filtersForAccount = filtersForAccount?.filter(
              ({ module }) => module !== Modules.Categories,
            );
          }

          responses = [
            this.#propertiesSharedService.getSupplierProperties(accountId),
            observableOf({
              status: true,
              data: {
                filters: filtersForAccount ?? defaultFilters,
              },
              message: '',
            }),
            isLegacyCategoriesModuleActivated ?
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: delete the comment and fix the error
              this.#categoryTreeService.getCategoryTrees({
                paginate: false,
                filter: { account_id: accountId },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: delete the comment and fix the error
              } as any)
            : responses[2],
          ];
        }

        // NOTE: Fetch all three data sources in parallel
        return observableForkJoin(responses).pipe(
          map(([properties, activeFilters, categoryTrees]) => {
            if (
              properties.status &&
              activeFilters.status &&
              categoryTrees.status
            ) {
              // NOTE: Enrich slim filters with full data from properties BEFORE passing to reducer
              const contentAreas =
                this.#authService.profile?.contentAreas || [];

              const categoriesAreaId = contentAreas.find(
                (area) => area.name.toLowerCase() === Modules.Categories,
              );

              // NOTE: Extract typed data from successful API responses
              const propertiesData = properties.data;
              const categoryTreesData = categoryTrees.data;

              // NOTE: Add category trees to properties so category filters can be enriched
              const propertiesWithCategories =
                categoriesAreaId !== undefined && categoryTreesData.length > 0 ?
                  {
                    ...propertiesData,
                    [String(categoriesAreaId.id)]: categoryTreesData,
                  }
                : propertiesData;

              // NOTE: Enrich each slim filter with full property data and category information
              const activeFiltersData = activeFilters.data as
                | { filters?: Array<Partial<SupplierFilter>> }
                | undefined;
              const slimFilters = activeFiltersData?.filters ?? [];
              const enrichedFiltersArray = slimFilters.map((slimFilter) =>
                enrichSupplierFilter(
                  slimFilter,
                  propertiesWithCategories,
                  categoriesAreaId?.id,
                ),
              );

              const enrichedFilters = {
                ...activeFilters,
                data: {
                  ...activeFiltersData,
                  filters: enrichedFiltersArray,
                },
              };

              return getPropertiesAndFiltersSuccess({
                payload: {
                  properties,
                  activeFilters: enrichedFilters,
                  categoryTrees,
                },
              });
            }
            return getPropertiesAndFiltersFail({
              payload: [properties, activeFilters, categoryTrees].find(
                (r) => !r.status || r instanceof HttpErrorResponse,
              )!,
            });
          }),
          catchError((error) =>
            observableOf(
              getPropertiesAndFiltersFail({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
                payload: error,
              }),
            ),
          ),
        );
      }),
    );
  });

  readonly updateSupplierFilters$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(updateSupplierFilters),
      switchMap(({ payload }) => {
        const { accountId, filters } = payload;
        const slimFilters = filters.map((filter) => slimSupplierFilter(filter));

        return this.#suppliersService
          .saveSupplierFilters(accountId, slimFilters)
          .pipe(
            map((response) => {
              if (response.status) {
                this.#authService.setProfile({
                  // eslint-disable-next-line @typescript-eslint/no-misused-spread
                  ...this.#authService.profile,
                  activeFiltersPerAccount: {
                    ...this.#authService.profile.activeFiltersPerAccount,
                    [accountId]: filters.map((filter) =>
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                      SupplierManagementHelperService.instance.setActive(
                        filter,
                      ),
                    ),
                  },
                });

                return updateSupplierFiltersSuccess({
                  payload: {
                    filters,
                    message: this.#translate.translate(
                      'supplierManagement.successUpdateSupplierFilter',
                    ),
                  },
                });
              }

              return updateSupplierFiltersFail({
                payload: response,
              });
            }),
            catchError((error) =>
              observableOf(
                updateSupplierFiltersFail({
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  payload: error,
                }),
              ),
            ),
          );
      }),
    );
  });

  readonly showSuccessMessage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(updateSupplierFiltersSuccess),
      map((data) => showSuccessMessage({ payload: data })),
    );
  });

  readonly showErrorMessage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(updateSupplierFiltersFail),
      map((data) => showFailMessage({ payload: data })),
    );
  });
}
